import type { FC } from 'react'

import { InfoBox, LoaderWrapper, Popup, StyledText } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { Button } from '@skytech/ui/Button'
import { Disclosure } from '@skytech/ui/Disclosure'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type { Asset } from '@/modules/admin/resources'
import { leasingAssetActions } from '@/modules/admin/resources/assets/constants'
import type {
  EndOfLeaseAssetsValidate,
  ExtendAssetsValidate,
  ReturnAssetsValidate,
} from '@/resources/leasing'

import { useLeasedAssets } from '../AssetsLeased.context'

export type ActionAssets = {
  message?: string
} & Pick<Asset, 'id' | 'assetDescription'>

export const ActionPopup: FC<{
  assets: Asset[]
}> = ({ assets }) => {
  const translate = useTranslation()
  const {
    action,
    isActionPopupOpen,
    selectedList,
    unavailableAssets,
    isLoadingValidation,
    isErrorValidation,
    isSuccessValidation,
    isLoadingConfirm,
    isErrorConfirm,
    isSuccessConfirm,
    onConfirmClick,
    clearSelectedList,
    setIsActionPopupOpen,
    resetMutations,
  } = useLeasedAssets()
  if (!action) throw Error(translate('Missing selected action'))

  const availableAssetsCount = Math.max(selectedList.length - unavailableAssets.length, 0)
  const isConfirmDisabled = selectedList.length <= unavailableAssets.length
  const { popupTitle, errorText } = getActionTranslations(action)

  const handleOnClose = () => {
    resetMutations()
    clearSelectedList()
    setIsActionPopupOpen(false)
  }

  return (
    <Popup
      variant="medium"
      ariaLabel={translate('Confirm the action')}
      isOpen={isActionPopupOpen}
      onClose={() => handleOnClose()}
    >
      {popupTitle && <h2 className="text-left">{popupTitle}</h2>}
      <LoaderWrapper isLoading={isLoadingValidation}>
        {isErrorValidation && (
          <InfoBox
            emphasis="error"
            className={twJoin(
              'flex flex-col items-start justify-start gap-1',
              'rounded-md border-y-[0.5px] border-l-4 border-r-[0.5px]'
            )}
          >
            <StyledText colorMode="red" size="regular-plus" fontWeight="bold">
              <Translation id="API Error! Please try again later." />
            </StyledText>
          </InfoBox>
        )}
        {isSuccessValidation &&
          (isSuccessConfirm ? (
            <UpdatedSuccess onClick={() => handleOnClose()} />
          ) : (
            <div className="flex flex-col gap-6">
              <div>
                <InfoBox
                  emphasis={availableAssetsCount === selectedList.length ? 'success' : 'warning'}
                  className={twJoin(
                    'flex flex-col items-start justify-start gap-1',
                    'rounded-md border-y-[0.5px] border-l-4 border-r-[0.5px]'
                  )}
                >
                  <StyledText
                    colorMode={availableAssetsCount === selectedList.length ? 'green' : 'warning'}
                    size="regular-plus"
                    fontWeight="bold"
                  >
                    {availableAssetsCount > 0 ? (
                      <>
                        {availableAssetsCount === selectedList.length ? (
                          <Translation
                            id="{count} assets will be processed"
                            count={String(availableAssetsCount)}
                          />
                        ) : (
                          <Translation
                            id="{count} of {totalCount} assets will be processed"
                            count={String(availableAssetsCount)}
                            totalCount={String(selectedList.length)}
                          />
                        )}
                      </>
                    ) : (
                      <Translation id="None of the selected assets are eligible for requested action" />
                    )}
                  </StyledText>

                  {unavailableAssets.length > 0 && (
                    <StyledText
                      colorMode={availableAssetsCount === selectedList.length ? 'green' : 'warning'}
                      size="sm"
                    >
                      {availableAssetsCount > 0 ? (
                        <Translation
                          id="{count} {count, plural, one {asset is} other {assets are}} blocked and will be skipped."
                          count={String(unavailableAssets.length)}
                        />
                      ) : (
                        <Translation id="Resolve the issues below, or go back and change your selection." />
                      )}
                    </StyledText>
                  )}
                </InfoBox>
              </div>
              {unavailableAssets.length > 0 && (
                <UnavailableAssets assets={assets} unavailableAssets={unavailableAssets} />
              )}
              {isErrorConfirm && (
                <InfoBox
                  emphasis="error"
                  className={twJoin(
                    'flex flex-col items-start justify-start gap-1',
                    'rounded-md border-y-[0.5px] border-l-4 border-r-[0.5px]'
                  )}
                >
                  <StyledText colorMode="red" size="regular-plus" fontWeight="bold">
                    {errorText}
                  </StyledText>
                </InfoBox>
              )}

              <Buttons
                isConfirmDisabled={isConfirmDisabled}
                isLoadingConfirm={isLoadingConfirm}
                handleOnClose={handleOnClose}
                onClick={() => onConfirmClick()}
              />
            </div>
          ))}
      </LoaderWrapper>
    </Popup>
  )
}

const UnavailableAssets: FC<{
  assets: Asset[]
  unavailableAssets: ExtendAssetsValidate | ReturnAssetsValidate | EndOfLeaseAssetsValidate
}> = ({ assets, unavailableAssets }) => {
  const groupedByMessage = unavailableAssets?.reduce<Record<string, number[]>>((acc, item) => {
    if (!acc[item.message]) {
      acc[item.message] = []
    }
    acc[item.message].push(item.assetId)
    return acc
  }, {})

  return (
    <div>
      <h3 className="m-0 mb-2 p-0 text-sm font-bold tracking-wide text-zinc-500">
        <Translation id="BLOCKED ASSETS" className="font-bold uppercase" /> ·{' '}
        {unavailableAssets.length}
      </h3>
      <div className="flex flex-col gap-4">
        {Object.entries(groupedByMessage).map(([message, assetIds]) => {
          const assetsByMessage = assets.filter(a => assetIds.includes(a.id))
          if (assetsByMessage.length === 0) return null

          return (
            <Disclosure key={message} defaultOpen={false}>
              {({ open }) => (
                <div
                  className={twJoin(
                    'transition-max-height rounded-md',
                    open ? 'max-h-[99999px]' : 'max-h-48'
                  )}
                >
                  <Disclosure.Button
                    isOpen={open}
                    className={twJoin(
                      'border-[0.5px] border-solid border-[#ccc] bg-zinc-100 px-4 py-2',
                      open ? 'rounded-b-none' : ''
                    )}
                  >
                    <div className="flex w-full flex-wrap items-center justify-between gap-0.5">
                      <StyledText block size="regular-plus" colorMode="black" fontWeight="bold">
                        {message && message !== 'null' ? (
                          <Translation id={`lease-action-blocker/${message}`} />
                        ) : (
                          <Translation id="lease-action-blocker/Not eligible for action" />
                        )}
                      </StyledText>
                      <Badge type="info" size="small">
                        <StyledText colorMode="black" fontWeight="bold" size="small">
                          {assetsByMessage.length}
                        </StyledText>
                      </Badge>
                    </div>
                  </Disclosure.Button>

                  <Disclosure.Panel
                    className={twJoin(
                      'pt-2',
                      open &&
                        'rounded-md rounded-t-none border-[0.5px] border-t-0 border-solid border-[#ccc]'
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      {assetsByMessage.map(asset => (
                        <div key={asset.id} className="whitespace-pre-line">
                          <StyledText block size="regular" colorMode="black" className="!inline">
                            {asset.assetDescription}
                            {Boolean(asset.costObjectName) && ', '}
                          </StyledText>
                          {Boolean(asset.costObjectName) && (
                            <StyledText block size="regular" colorMode="gray" className="!inline">
                              {asset.costObjectName}
                            </StyledText>
                          )}
                        </div>
                      ))}
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          )
        })}
      </div>
    </div>
  )
}
const Buttons: FC<{
  isConfirmDisabled: boolean
  isLoadingConfirm: boolean
  handleOnClose: (open: boolean) => void
  onClick: () => void
}> = ({ isConfirmDisabled, isLoadingConfirm, handleOnClose, onClick }) => {
  const { whiteLabel } = useAppContext()
  const isDisabled = isLoadingConfirm || isConfirmDisabled

  return (
    <div className="mt-4 flex items-center justify-end">
      <div className="self-end">
        <Button variant="secondary" onClick={() => handleOnClose(false)}>
          <Translation id="Cancel" />
        </Button>

        <Button
          className={twJoin(
            'ml-8 mr-0',
            isDisabled && whiteLabel === 'mobit' && 'disabled:brightness-100'
          )}
          variant="primary"
          disabled={isDisabled}
          onClick={e => {
            e.stopPropagation()
            onClick()
          }}
        >
          {isLoadingConfirm ? <Translation id="Saving..." /> : <Translation id="Confirm" />}
        </Button>
      </div>
    </div>
  )
}

const UpdatedSuccess: FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className="mt-10">
    <h3 className="center m-5">
      <Translation id="Selected assets successfully updated." />
    </h3>
    <div className="flex justify-center">
      <Button
        variant="secondary"
        onClick={e => {
          e.stopPropagation()
          onClick()
        }}
      >
        <Translation id="Close" />
      </Button>
    </div>
  </div>
)

const getActionTranslations = (
  action: string | number
): { popupTitle: JSX.Element; errorText: JSX.Element } => {
  switch (action) {
    case leasingAssetActions.LEASE_END: {
      return {
        popupTitle: <Translation id="Replace assets" />,
        errorText: <Translation id="We couldn't replace assets. Please try again later." />,
      }
    }
    case leasingAssetActions.EMPLOYEE_SELECTION: {
      return {
        popupTitle: <Translation id="End of lease employee selection" />,
        errorText: (
          <Translation id="We couldn't start employee selection for assets. Please try again later." />
        ),
      }
    }
    case leasingAssetActions.RETURN:
      return {
        popupTitle: <Translation id="Return assets" />,
        errorText: <Translation id="We couldn't return assets. Please try again later." />,
      }

    // Extend assets option
    default:
      return {
        popupTitle: (
          <Translation
            id="Extend assets for {numberOfMonths} {numberOfMonths, plural, one {month} other {months}}"
            numberOfMonths={String(action)}
          />
        ),
        errorText: <Translation id="We couldn't extend assets. Please try again later." />,
      }
  }
}
